// Pure digest logic: group loads, suggested focus, and the WhatsApp text.
import type { DigestPayload, Taxonomy } from './types.ts';

/** Same model as SQL muscle_load() and RecoverCore LoadModel: (intensity/5) * 0.5^(hours/48). */
export function loadContribution(intensity: number, hoursAgo: number): number {
  return (intensity / 5) * Math.pow(0.5, Math.max(0, hoursAgo) / 48);
}

export type LoadLevel = 'high' | 'moderate' | 'light' | 'fresh';

export function loadLevel(load: number): LoadLevel {
  if (load >= 0.8) return 'high';
  if (load >= 0.4) return 'moderate';
  if (load > 0) return 'light';
  return 'fresh';
}

/** Major trainable groups, in the order focus suggestions are offered. */
export const MAJOR_GROUPS = ['back', 'hamstrings', 'glutes', 'chest', 'quads', 'shoulders', 'core', 'triceps', 'biceps', 'calves'];

export interface GroupLoad {
  group_id: string;
  load: number;
  level: LoadLevel;
  last_worked_at: string | null;
}

/** Group load = highest load among its muscles (a group is as tired as its most-worked muscle). */
export function groupLoads(payload: DigestPayload): Map<string, GroupLoad> {
  const out = new Map<string, GroupLoad>();
  for (const l of payload.load) {
    const prev = out.get(l.group_id);
    const last = prev?.last_worked_at && prev.last_worked_at > l.last_worked_at ? prev.last_worked_at : l.last_worked_at;
    const load = Math.max(prev?.load ?? 0, Number(l.load));
    out.set(l.group_id, { group_id: l.group_id, load, level: loadLevel(load), last_worked_at: last });
  }
  return out;
}

export interface Focus {
  focus: string[];
  easy: string[];
  lighterSession: boolean;
}

export function suggestFocus(payload: DigestPayload): Focus {
  const loads = groupLoads(payload);
  const levelOf = (g: string) => loads.get(g)?.level ?? 'fresh';
  const fresh = MAJOR_GROUPS.filter((g) => levelOf(g) === 'fresh');
  const light = MAJOR_GROUPS.filter((g) => levelOf(g) === 'light');
  const focus = [...fresh, ...light].slice(0, 3);
  const easy = [...loads.values()]
    .filter((g) => g.level === 'high')
    .sort((a, b) => b.load - a.load)
    .map((g) => g.group_id);
  const lighterSession = (payload.latest_recovery?.rating ?? 5) <= 2;
  return { focus, easy, lighterSession };
}

function fmtDate(iso: string, tz: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat('en-GB', { timeZone: tz, ...opts }).format(new Date(iso));
}

function hoursBetween(a: string, b: string): number {
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}

const ACTIVITY_LABEL: Record<string, string> = {
  strength: 'Strength', running: 'Run', walking: 'Walk', cycling: 'Ride', swimming: 'Swim',
  hiit: 'HIIT', yoga: 'Yoga', pilates: 'Pilates', rowing: 'Row', hiking: 'Hike', other: 'Workout',
};

export function activityLabel(activity: string): string {
  return ACTIVITY_LABEL[activity] ?? activity.charAt(0).toUpperCase() + activity.slice(1);
}

export function groupNames(taxonomy: Taxonomy, muscleIds: string[]): string[] {
  const groupOf = new Map(taxonomy.muscles.map((m) => [m.id, m.group]));
  const nameOf = new Map(taxonomy.groups.map((g) => [g.id, g.name]));
  const order = taxonomy.groups.map((g) => g.id);
  const groups = [...new Set(muscleIds.map((id) => groupOf.get(id)).filter((g): g is string => !!g))];
  return groups.sort((a, b) => order.indexOf(a) - order.indexOf(b)).map((g) => nameOf.get(g) ?? g);
}

/** Daily check-in as the trainee chose it (apps offer Sore / Okay / Ready to train, stored as 2 / 3 / 5). */
export function checkinWord(rating: number): string {
  return rating <= 2 ? 'sore' : rating <= 4 ? 'okay' : 'ready to train';
}

export interface DigestOptions {
  /** Upcoming session start, shown in the header. */
  startsAt?: string;
}

/** Full digest text (WhatsApp markdown). Honours sharing switches already applied by SQL. */
export function formatDigest(payload: DigestPayload, taxonomy: Taxonomy, opts: DigestOptions = {}): string {
  const tz = payload.trainee.timezone || 'Asia/Jerusalem';
  const nameOf = new Map(taxonomy.groups.map((g) => [g.id, g.name]));
  const lines: string[] = [];
  const when = opts.startsAt
    ? ` · session ${fmtDate(opts.startsAt, tz, { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`
    : '';
  lines.push(`*${payload.trainee.full_name || 'Your trainee'}*${when}`);

  // Sessions this week.
  const sessions = payload.sessions;
  lines.push('', `*Last 7 days* · ${sessions.length} session${sessions.length === 1 ? '' : 's'}`);
  if (!sessions.length) lines.push('No logged sessions.');
  for (const s of sessions) {
    const day = fmtDate(s.started_at, tz, { weekday: 'short', day: 'numeric', month: 'short' });
    const kind = s.kind === 'trainer' ? 'with trainer' : `${activityLabel(s.activity_type)} (solo)`;
    const parts = [`${day} · ${kind}`];
    if (s.intensity != null) parts.push(`intensity ${s.intensity}/5`);
    const groups = groupNames(taxonomy, s.muscle_ids);
    if (groups.length) parts.push(groups.join(', '));
    const h = s.health ?? {};
    const health = [
      h.duration_min ? `${Math.round(h.duration_min)} min` : '',
      h.distance_km ? `${h.distance_km.toFixed(1)} km` : '',
      h.avg_hr ? `avg HR ${Math.round(h.avg_hr)}` : '',
    ].filter(Boolean);
    if (health.length) parts.push(health.join(', '));
    lines.push(`• ${parts.join(' · ')}`);
  }

  if (payload.share.ratings && payload.latest_recovery) {
    const r = payload.latest_recovery.rating;
    const day = fmtDate(`${payload.latest_recovery.day}T12:00:00Z`, tz, { weekday: 'short' });
    lines.push('', `*Feeling* (${day}): ${checkinWord(r)}`);
  }

  if (payload.share.muscles) {
    const loads = [...groupLoads(payload).values()].sort((a, b) => b.load - a.load);
    if (loads.length) {
      const fmt = loads.slice(0, 5).map((g) => {
        const ago = g.last_worked_at ? Math.round(hoursBetween(g.last_worked_at, payload.as_of) / 24) : null;
        const agoTxt = ago == null ? '' : ago === 0 ? ', today' : `, ${ago}d ago`;
        return `${nameOf.get(g.group_id) ?? g.group_id} (${g.level}${agoTxt})`;
      });
      lines.push('', `*Most worked*: ${fmt.join(', ')}`);
    }
    const f = suggestFocus(payload);
    const named = (ids: string[]) => ids.map((g) => nameOf.get(g) ?? g).join(', ');
    const tips: string[] = [];
    if (f.focus.length) tips.push(`fresh: ${named(f.focus)}`);
    if (f.easy.length) tips.push(`go easy on ${named(f.easy)}`);
    if (f.lighterSession) tips.push('recovery is low, consider a lighter session');
    if (tips.length) lines.push(`*Suggested*: ${tips.join('; ')}`);
  }

  if (payload.notes_to_raise.length) {
    lines.push('', '*Your notes to raise*');
    for (const n of payload.notes_to_raise) lines.push(`• ${n.body}`);
  }
  return lines.join('\n');
}

export function formatStartTime(iso: string, tz = 'Asia/Jerusalem'): string {
  return fmtDate(iso, tz, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}
